import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  getUserGamificationStats, 
  getGames, 
  getGameById, 
  submitGameScore,
  getLeaderboard 
} from '../../services/gamesAPI';
import { 
  Award, 
  Lightbulb, 
  Puzzle, 
  X, 
  CheckCircle, 
  AlertTriangle, 
  Info,
  Trophy,
  Target,
  Clock,
  Star,
  Play,
  Users
} from 'lucide-react';
import toast from 'react-hot-toast';

const GamificationDashboard = () => {
  const { user } = useSelector((state) => state.profile);
  const navigate=useNavigate()
  const [stats, setStats] = useState({
    user: { points: 0, badges: [] },
    gameStats: [],
    recentScores: [],
    nextBadge: null,
    badgeThresholds: []
  });
  const [games, setGames] = useState([]);
  const [leaderboard, setLeaderboard] = useState({ leaderboard: [], userRank: null });
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [isGameModalOpen, setIsGameModalOpen] = useState(false);
  const [isLeaderboardModalOpen, setIsLeaderboardModalOpen] = useState(false);
  const [isPointsInfoModalOpen, setIsPointsInfoModalOpen] = useState(false);

  // Game state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [gameScore, setGameScore] = useState(0);
  const [foundHazards, setFoundHazards] = useState([]);
  const [selectedScenarioOption, setSelectedScenarioOption] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, gamesRes, leaderboardRes] = await Promise.all([
        getUserGamificationStats(),
        getGames(),
        getLeaderboard('all')
      ]);

      setStats(statsRes);
      setGames(gamesRes);
      setLeaderboard(leaderboardRes);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGame = async (game) => {
    try {
      const gameDetails = await getGameById(game._id);
      setGameData(gameDetails);
      setSelectedGame(game);
      setIsGameModalOpen(true);
      resetGameState();
      setGameStartTime(Date.now());
    } catch (error) {
      console.error('Error fetching game details:', error);
      toast.error('Failed to load game');
    }
  };

  const resetGameState = () => {
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setGameCompleted(false);
    setGameScore(0);
    setFoundHazards([]);
    setSelectedScenarioOption(null);
  };

  const handleQuizAnswer = (questionIndex, answer) => {
    setUserAnswers(prev => ({ ...prev, [questionIndex]: answer }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < gameData.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleSubmitQuiz();
    }
  };

  const handleSubmitQuiz = async () => {
    try {
      let score = 0;
      let maxScore = 0;
      const answers = [];

      gameData.questions.forEach((question, index) => {
        const userAnswer = userAnswers[index];
        const isCorrect = userAnswer === question.correctAnswer;
        const pointsEarned = isCorrect ? question.points : 0;
        
        score += pointsEarned;
        maxScore += question.points;
        
        answers.push({
          questionId: index.toString(),
          userAnswer: userAnswer || '',
          isCorrect,
          pointsEarned
        });
      });

      const timeTaken = Math.floor((Date.now() - gameStartTime) / 1000);
      
      const result = await submitGameScore(gameData._id, {
        score,
        maxScore,
        timeTaken,
        answers
      });

      setGameScore(result.percentage);
      setGameCompleted(true);
      
      toast.success(`Quiz completed! You earned ${result.pointsEarned} points!`);
      
      if (result.newBadges && result.newBadges.length > 0) {
        toast.success(`New badges earned: ${result.newBadges.join(', ')}`);
      }

      // Refresh stats
      fetchDashboardData();
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz');
    }
  };

  
  const handleHazardClick = (hazard) => {
   
    if (!foundHazards.find(h => h._id === hazard._id)) {
      setFoundHazards(prev => [...prev, hazard]);
      setGameScore(prev => prev + hazard.points);
    }
  
  };

  const handleSubmitHazardGame = async () => {
    try {
      const totalPossiblePoints = gameData.hazards.reduce((sum, h) => sum + h.points, 0);
      const timeTaken = Math.floor((Date.now() - gameStartTime) / 1000);
      
      const result = await submitGameScore(gameData._id, {
        score: gameScore,
        maxScore: totalPossiblePoints,
        timeTaken,
        answers: foundHazards.map(h => ({
          questionId: h._id.toString(),
          userAnswer: 'found',
          isCorrect: true,
          pointsEarned: h.points
        }))
      });

      setGameCompleted(true);
      toast.success(`Hazard game completed! You earned ${result.pointsEarned} points!`);
      
      if (result.newBadges && result.newBadges.length > 0) {
        toast.success(`New badges earned: ${result.newBadges.join(', ')}`);
      }

      fetchDashboardData();
    } catch (error) {
      console.error('Error submitting hazard game:', error);
      toast.error('Failed to submit game');
    }
  };

  const handleSubmitScenario = async () => {
    try {
      const selectedOption = gameData.scenarioOptions.find(opt => opt._id === selectedScenarioOption);
      const pointsEarned = selectedOption?.isCorrect ? selectedOption.points : 0;
      const timeTaken = Math.floor((Date.now() - gameStartTime) / 1000);
      
      const result = await submitGameScore(gameData._id, {
        score: pointsEarned,
        maxScore: Math.max(...gameData.scenarioOptions.map(opt => opt.points)),
        timeTaken,
        answers: [{
          questionId: '1',
          userAnswer: selectedOption?.text || '',
          isCorrect: selectedOption?.isCorrect || false,
          pointsEarned
        }]
      });

      setGameCompleted(true);
      setGameScore(result.percentage);
      
      toast.success(`Scenario completed! You earned ${result.pointsEarned} points!`);
      
      if (result.newBadges && result.newBadges.length > 0) {
        toast.success(`New badges earned: ${result.newBadges.join(', ')}`);
      }

      fetchDashboardData();
    } catch (error) {
      console.error('Error submitting scenario:', error);
      toast.error('Failed to submit scenario');
    }
  };

  const renderGameContent = () => {
    if (!gameData) return null;

    switch (gameData.type) {
      case 'quiz':
        return renderQuizGame();
      case 'spot-hazard':
        return renderHazardGame();
      case 'safety-scenario':
        return renderScenarioGame();
      default:
        return <div>Unknown game type</div>;
    }
  };

  const renderQuizGame = () => {
    if (gameCompleted) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Quiz Completed!</h3>
          <p className="text-lg text-gray-600 mb-4">Your Score: {gameScore}%</p>
          <button
            onClick={() => setIsGameModalOpen(false)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Close
          </button>
        </div>
      );
    }

    const currentQuestion = gameData.questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / gameData.questions.length) * 100;

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Question {currentQuestionIndex + 1} of {gameData.questions.length}
          </span>
          <div className="w-32 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">{currentQuestion.question}</h3>
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => (
              <label key={index} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={`question-${currentQuestionIndex}`}
                  value={option}
                  checked={userAnswers[currentQuestionIndex] === option}
                  onChange={() => handleQuizAnswer(currentQuestionIndex, option)}
                  className="mr-3 h-4 w-4 text-blue-600"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleNextQuestion}
            disabled={!userAnswers[currentQuestionIndex]}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg"
          >
            {currentQuestionIndex === gameData.questions.length - 1 ? 'Submit Quiz' : 'Next Question'}
          </button>
        </div>
      </div>
    );
  };

  const renderHazardGame = () => {
    if (gameCompleted) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">All Hazards Found!</h3>
          <p className="text-lg text-gray-600 mb-4">Total Points: {gameScore}</p>
          <button
            onClick={() => setIsGameModalOpen(false)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Close
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Find all the hazards in the image below</p>
          <p className="text-lg font-semibold">Score: {gameScore} points</p>
        </div>

        <div className="relative">
          <img
            src={gameData.hazardImage}
            alt="Hazard Scene"
            className="w-full rounded-lg"
          />
          {gameData.hazards.map((hazard) => (
            <button
              key={hazard.id}
              onClick={() => handleHazardClick(hazard)}
              disabled={foundHazards.find(h => h._id === hazard._id)}
              className={`absolute border-2 rounded ${
                foundHazards.find(h => h._id === hazard._id)
                  ? 'border-green-500 bg-green-200'
                  : 'border-red-500 bg-red-200 hover:bg-red-300'
              }`}
              style={{
                left: `${hazard.x}%`,
                top: `${hazard.y}%`,
                width: `${hazard.width}%`,
                height: `${hazard.height}%`
              }}
              title={foundHazards.find(h => h._id === hazard._id) ? hazard.description : 'Click to identify hazard'}
            />
          ))}
        </div>

        <div className="space-y-2">
          <h4 className="font-semibold">Hazards to Find:</h4>
          {gameData.hazards.map((hazard) => (
            <div
              key={hazard.id}
              className={`flex items-center justify-between p-2 rounded ${
                foundHazards.find(h => h._id === hazard._id)
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100'
              }`}
            >
              <span>{hazard.description}</span>
              <span className="font-semibold">{hazard.points} pts</span>
              {foundHazards.find(h => h._id === hazard._id) && (
                <CheckCircle className="h-5 w-5 text-green-600" />
              )}
            </div>
          ))}
        </div>

        {foundHazards.length === gameData.hazards.length && (
          <div className="text-center">
            <button
              onClick={handleSubmitHazardGame}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg"
            >
              Submit Game
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderScenarioGame = () => {
    if (gameCompleted) {
      const selectedOption = gameData.scenarioOptions.find(opt => opt._id === selectedScenarioOption);
      return (
        <div className="text-center py-8">
          <div className={`mx-auto h-16 w-16 mb-4 ${selectedOption?.isCorrect ? 'text-green-500' : 'text-red-500'}`}>
            {selectedOption?.isCorrect ? <CheckCircle /> : <X />}
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Scenario Completed!</h3>
          <p className="text-lg text-gray-600 mb-4">Your Score: {gameScore}%</p>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <p className="font-semibold mb-2">Feedback:</p>
            <p>{selectedOption?.feedback}</p>
          </div>
          <button
            onClick={() => setIsGameModalOpen(false)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Close
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Scenario:</h3>
          <p>{gameData.scenario}</p>
        </div>

        <div className="space-y-3">
          <h4 className="font-semibold">What would you do?</h4>
          {gameData.scenarioOptions.map((option) => (
            <label key={option._id} className="flex items-start cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
              <input
                type="radio"
                name="scenario-option"
                value={option._id}
                checked={selectedScenarioOption === option._id}
                onChange={() => setSelectedScenarioOption(option._id)}
                className="mr-3 mt-1 h-4 w-4 text-blue-600"
              />
              <span>{option.text}</span>
            </label>
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={handleSubmitScenario}
            disabled={!selectedScenarioOption}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg"
          >
            Submit Answer
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-lg text-gray-700">Loading Gaming Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen  lg:p-4 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-blue-700 mb-2">Gaming Dashboard</h1>
            <p className="text-gray-600">Play safety games, earn points, and compete with your colleagues</p>
          </div>
          
          <div className='flex gap-2'>
          <button 
            onClick={() => setIsPointsInfoModalOpen(true)} 
            className="text-blue-600 hover:text-blue-800 transition-colors"
          >
            <Info size={24} />
          </button>
          {user.accountType === 'Admin' && (
            <button
            className='bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg'
            onClick={()=>navigate("/bbs/games/create")}>
              Create Game
            </button>
          )}
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Points</p>
                <p className="text-3xl font-bold text-blue-600">{stats.user.points}</p>
              </div>
              <Award className="h-12 w-12 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Badges Earned</p>
                <p className="text-3xl font-bold text-purple-600">{stats.user.badges.length}</p>
              </div>
              <Trophy className="h-12 w-12 text-purple-500" />
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Games Played</p>
                <p className="text-3xl font-bold text-green-600">
                  {stats.gameStats.reduce((sum, stat) => sum + stat.gamesPlayed, 0)}
                </p>
              </div>
              <Play className="h-12 w-12 text-green-500" />
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Plant Rank</p>
                <p className="text-3xl font-bold text-orange-600">
                  {leaderboard.userRank ? `#${leaderboard.userRank}` : 'N/A'}
                </p>
              </div>
              <Target className="h-12 w-12 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Badges Display */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <Award className="mr-2 text-yellow-500" />
            Your Badges
          </h2>
          <div className="flex flex-wrap gap-3">
            {stats.user.badges && stats.user.badges.length > 0 ? (
              stats.user.badges.map((badge, index) => (
                <span
                  key={index}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg"
                >
                  {badge}
                </span>
              ))
            ) : (
              <p className="text-gray-500 italic">No badges earned yet. Play games to earn them!</p>
            )}
          </div>
          {stats.nextBadge && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Next Badge:</strong> {stats.nextBadge.badge} 
                <span className="ml-2">({stats.nextBadge.points - stats.user.points} points to go)</span>
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => setIsLeaderboardModalOpen(true)}
            className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center"
          >
            <Users className="mr-2" size={20} />
            View Leaderboard
          </button>
        </div>

        {/* Available Games */}
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Available Games</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <motion.div
                key={game._id}
                whileHover={{ scale: 1.02, y: -5 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => handleSelectGame(game)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${
                    game.type === 'quiz' ? 'bg-blue-100' :
                    game.type === 'spot-hazard' ? 'bg-green-100' :
                    'bg-yellow-100'
                  }`}>
                    {game.type === 'quiz' ? <Lightbulb className="h-6 w-6 text-blue-600" /> :
                     game.type === 'spot-hazard' ? <Puzzle className="h-6 w-6 text-green-600" /> :
                     <AlertTriangle className="h-6 w-6 text-yellow-600" />}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    game.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                    game.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {game.difficulty}
                  </span>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{game.title}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{game.description}</p>
                
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span className="flex items-center">
                    <Star className="h-4 w-4 mr-1" />
                    {game.pointsReward} points
                  </span>
                  <span className="flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    {game.totalPlays} plays
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Game Modal */}
        <AnimatePresence>
          {isGameModalOpen && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">{gameData?.title}</h2>
                  <button 
                    onClick={() => setIsGameModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={24} />
                  </button>
                </div>
                
                {renderGameContent()}
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Leaderboard Modal */}
        <AnimatePresence>
          {isLeaderboardModalOpen && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                    <Trophy className="mr-2 text-yellow-500" />
                    Plant Leaderboard
                  </h2>
                  <button 
                    onClick={() => setIsLeaderboardModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={24} />
                  </button>
                </div>
                
                <div className="space-y-3">
                  {leaderboard.leaderboard.map((player, index) => (
                    <div
                      key={player._id}
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        player._id === user._id ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-500 text-white' :
                          index === 1 ? 'bg-gray-400 text-white' :
                          index === 2 ? 'bg-orange-500 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>
                          {player.rank}
                        </div>
                        <div>
                          <p className="font-semibold">{player.firstName} {player.lastName}</p>
                          <p className="text-sm text-gray-500">{player.gamesPlayed} games played</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{player.totalPoints}</p>
                        <p className="text-sm text-gray-500">points</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Points Info Modal */}
        <AnimatePresence>
          {isPointsInfoModalOpen && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-xl p-6 w-full max-w-md"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                    <Info className="mr-2 text-blue-500" />
                    How to Earn Points
                  </h2>
                  <button 
                    onClick={() => setIsPointsInfoModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={24} />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Badge Thresholds:</h3>
                    <div className="space-y-2">
                      {stats.badgeThresholds.map((threshold, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-sm">{threshold.badge}</span>
                          <span className="font-semibold text-blue-600">{threshold.points} pts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GamificationDashboard;